import { Box } from "@mui/material"
import { type Row, getCoreRowModel } from "@tanstack/react-table"
import type { FC } from "react"
import { useRef } from "react"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { Contact } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useCompanyContactsTable } from "@/screens/Companies/edit/components/contacts/useCompanyContactsTable"

interface ContactsTableProps {
  fetching: boolean
  data: Contact[]
  setIsCommentDialogOpen: (open: boolean) => void
  setSelectedContact: (contact: Contact) => void
  onContactClick: (contact: Contact) => void
  throttledLoadMore: () => void
}

const ContactsTable: FC<ContactsTableProps> = ({
  setIsCommentDialogOpen,
  throttledLoadMore,
  setSelectedContact,
  onContactClick,
  fetching,
  data,
}) => {
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const { columns } = useCompanyContactsTable({
    onCommentClick: (contact) => {
      setIsCommentDialogOpen(true)
      setSelectedContact(contact)
    },
    onViewClick: onContactClick,
  })

  const scrollHandler = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  return (
    <Box className='flex h-full flex-col overflow-y-hidden'>
      <AerosBaseTable<Contact>
        features={{
          table: {
            stickyHeader: true,
          },
          tanstackOptions: {
            data,
            columns,
            state: {
              isLoading: fetching,
            },
            getCoreRowModel: getCoreRowModel(),
            manualSorting: true,
            enableSortingRemoval: false,
          },
        }}
        slotProps={{
          container: {
            ref: tableContainerRef,
            onScroll: scrollHandler,
          },
          bodyRow: {
            className: "hover:bg-primary-100 cursor-pointer",
            onClick: (row) => onContactClick((row as Row<Contact>).original),
          },
        }}
        noDataMessage='No contacts found'
      />
    </Box>
  )
}

export default ContactsTable
